import { useState, useRef, type ReactNode } from 'react';
import { Phone, PhoneIncoming, PhoneMissed, PhoneOutgoing, Play, Loader2, Voicemail } from 'lucide-react';
import { useStoreMe, useEffectiveStoreNo, isNotAvailable } from '@/lib/useStores';
import { todayInStoreZone } from '@/lib/useToday';
import {
  useStoreVoicemails,
  useStoreCalls,
  fetchCallRecording,
  type CallRecord,
  type VoicemailRecord,
} from '@/lib/useComms';

// The API sends time as a preformatted label (e.g. '4:12 PM'); render as-is.
function formatTime(label: string): string {
  return label;
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

// --- Shared UI atoms ---

function CardShell({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-candy-line bg-candy-white shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
      {children}
    </div>
  );
}

function CardHeader({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2.5 border-b border-candy-line px-4 py-3 dark:border-candy-dark-line">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-candy-cream-warm text-candy-pink dark:bg-candy-dark-surface-warm dark:text-candy-pink-dark">
        {icon}
      </span>
      <span className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">{title}</span>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="px-4 py-6 text-center">
      <p className="text-[13px] text-candy-ink-muted dark:text-candy-dark-ink-muted">{message}</p>
    </div>
  );
}

const OUTCOME_LABELS: Record<string, string> = {
  live: 'Answered',
  voicemail: 'Voicemail',
  no_message: 'No message',
  after_hours: 'After hours',
  abandoned: 'Missed',
};

function OutcomeChip({ outcome, direction }: { outcome: string; direction: string }) {
  const missed = outcome === 'abandoned';
  const answered = outcome === 'live';

  const bg = missed
    ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
    : answered
      ? 'bg-candy-teal/10 text-candy-teal dark:text-candy-teal-dark'
      : 'bg-candy-cream-warm text-candy-ink-muted dark:bg-candy-dark-surface-warm dark:text-candy-dark-ink-muted';

  const Icon =
    missed ? PhoneMissed : direction === 'out' ? PhoneOutgoing : PhoneIncoming;

  return (
    <span className={`flex items-center gap-1 rounded-pill px-2 py-0.5 text-[11px] font-medium ${bg}`}>
      <Icon size={10} aria-hidden />
      {OUTCOME_LABELS[outcome] ?? outcome}
    </span>
  );
}

// --- Voicemail card ---

function VoicemailRow({ vm, storeNo }: { vm: VoicemailRecord; storeNo?: number }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  async function handlePlay() {
    setLoading(true);
    setError(false);
    try {
      const url = await fetchCallRecording(vm.linkedid, storeNo);
      if (!audioRef.current) {
        audioRef.current = new Audio(url);
      } else {
        audioRef.current.src = url;
      }
      audioRef.current.play().catch(() => setError(true));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex items-center gap-3 border-b border-candy-line px-4 py-3 last:border-0 dark:border-candy-dark-line">
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium text-candy-ink dark:text-candy-dark-ink truncate">
          {vm.caller_name ?? (vm.caller ? formatPhone(vm.caller) : 'Unknown')}
        </p>
        <p className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
          {vm.caller_name && vm.caller ? formatPhone(vm.caller) + ' · ' : ''}{formatTime(vm.time)}
        </p>
        {error && (
          <p className="text-[11px] text-red-500 dark:text-red-400">Could not load recording.</p>
        )}
      </div>
      {vm.has_recording && (
      <button
        onClick={handlePlay}
        disabled={loading}
        aria-label="Play voicemail"
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-candy-pink text-white hover:opacity-90 disabled:opacity-60 dark:bg-candy-pink-dark dark:text-candy-dark-bg"
      >
        {loading ? (
          <Loader2 size={14} className="animate-spin" aria-hidden />
        ) : (
          <Play size={14} aria-hidden />
        )}
      </button>
      )}
    </div>
  );
}

function VoicemailsCard({ storeNo }: { storeNo?: number }) {
  const { data, isLoading, error } = useStoreVoicemails(storeNo);

  return (
    <CardShell>
      <CardHeader icon={<Voicemail size={16} aria-hidden />} title="Voicemails to Return" />
      {isLoading ? (
        <div className="px-4 py-4">
          <div className="h-10 animate-pulse rounded-lg bg-candy-cream-warm dark:bg-candy-dark-surface-warm" />
        </div>
      ) : error && !isNotAvailable(error) ? (
        <EmptyState message="Could not load voicemails. Please try again." />
      ) : !data?.voicemails.length ? (
        <EmptyState message="No voicemails — this store's phone line has not moved to the new system yet." />
      ) : (
        <div>
          {data.voicemails.map((vm) => (
            <VoicemailRow key={vm.linkedid} vm={vm} storeNo={storeNo} />
          ))}
        </div>
      )}
    </CardShell>
  );
}

// --- Call list card ---

function CallRow({ call }: { call: CallRecord }) {
  return (
    <div className="flex items-center gap-3 border-b border-candy-line px-4 py-3 last:border-0 dark:border-candy-dark-line">
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium text-candy-ink dark:text-candy-dark-ink truncate">
          {call.caller_name ?? (call.caller ? formatPhone(call.caller) : 'Unknown')}
        </p>
        <p className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
          {call.caller_name && call.caller ? formatPhone(call.caller) + ' · ' : ''}{formatTime(call.time)}
          {call.talk_seconds ? ` · ${formatDuration(call.talk_seconds)}` : ''}
        </p>
      </div>
      <OutcomeChip outcome={call.outcome} direction={call.direction} />
    </div>
  );
}

function CallsCard({ storeNo }: { storeNo?: number }) {
  // Comms is HQ-facing with no single store's timezone in scope here, so the
  // shared helper's America/Denver fallback is the correct default.
  const [date, setDate] = useState(() => todayInStoreZone());
  const [direction, setDirection] = useState<'in' | 'out'>('in');

  const { data, isLoading, error } = useStoreCalls(storeNo, {
    date_from: date,
    date_to: date,
    direction,
  });

  return (
    <CardShell>
      <CardHeader icon={<Phone size={16} aria-hidden />} title="Call Log" />

      {/* Filters */}
      <div className="flex items-center gap-3 border-b border-candy-line px-4 py-3 dark:border-candy-dark-line">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="flex-1 rounded-md border border-candy-line bg-candy-white px-3 py-1.5 text-[13px] text-candy-ink focus:border-candy-pink focus:outline-none dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink dark:focus:border-candy-pink-dark"
        />
        <div className="flex rounded-md border border-candy-line overflow-hidden dark:border-candy-dark-line">
          {(['in', 'out'] as const).map((d) => (
            <button
              key={d}
              onClick={() => setDirection(d)}
              className={`px-3 py-1.5 text-[13px] font-medium capitalize transition-colors ${
                direction === d
                  ? 'bg-candy-pink text-white dark:bg-candy-pink-dark dark:text-candy-dark-bg'
                  : 'bg-candy-white text-candy-ink-muted hover:bg-candy-cream-warm dark:bg-candy-dark-surface dark:text-candy-dark-ink-muted dark:hover:bg-candy-dark-surface-warm'
              }`}
            >
              {d === 'in' ? 'Inbound' : 'Outbound'}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2 px-4 py-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-lg bg-candy-cream-warm dark:bg-candy-dark-surface-warm"
            />
          ))}
        </div>
      ) : error && !isNotAvailable(error) ? (
        <EmptyState message="Could not load calls. Please try again." />
      ) : !data?.calls.length ? (
        <EmptyState message="No calls yet — this store's phone line has not moved to the new system." />
      ) : (
        <div>
          {data.calls.map((call) => (
            <CallRow key={call.linkedid} call={call} />
          ))}
        </div>
      )}
    </CardShell>
  );
}

// --- Main export ---

export function Comms() {
  const activeStoreNo = useEffectiveStoreNo();

  const meQ = useStoreMe(activeStoreNo);
  const storeNo = activeStoreNo ?? meQ.data?.store?.store_no;
  const storeName = meQ.data?.store?.store_name;

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">Comms</h1>
        <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
          Calls &amp; voicemails
          {storeName && (
            <>
              {' · '}
              <span className="font-medium">{storeName}</span>
            </>
          )}
        </p>
      </div>

      <div className="space-y-4">
        <VoicemailsCard storeNo={storeNo} />
        <CallsCard storeNo={storeNo} />
      </div>
    </div>
  );
}
